import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Download, FileText, Calendar } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { ApiError } from "@/shared/lib/apiClient";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, inputClass, selectClass } from "../components/FormField";
import { monthOptions, yearOptions } from "../data";
import {
  exportGlobalInvoiceCsv,
  fetchAdminConventionsMeta,
  fetchAdminInvoiceHospitals,
  generateGlobalInvoice,
  type GlobalInvoiceResult,
} from "../api/adminApi";
import { getAuthToken } from "../auth";

const actTypeFilterOptions = [
  { value: "", label: "Toutes les natures" },
  { value: "forfait_mensuel", label: "Forfait mensuel" },
  { value: "hors_forfait", label: "Hors forfait" },
];

function formatUsd(value: number): string {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(value);
}

export function GlobalInvoicePage() {
  const { t } = useTranslation();
  const { showToast } = usePageActions();
  const labels = t.safecheck.invoices;

  const [companies, setCompanies] = useState<Array<{ id: number; name: string }>>([]);
  const [hospitals, setHospitals] = useState<Array<{ id: number; name: string }>>([]);
  const [companyId, setCompanyId] = useState("");
  const [hospitalId, setHospitalId] = useState("");
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [systemId, setSystemId] = useState("");
  const [actType, setActType] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<GlobalInvoiceResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!getAuthToken()) return;

    let cancelled = false;
    void (async () => {
      try {
        const [meta, hospitalRows] = await Promise.all([
          fetchAdminConventionsMeta(),
          fetchAdminInvoiceHospitals(),
        ]);
        if (cancelled) return;
        setCompanies(meta.companies ?? []);
        setHospitals(hospitalRows.map((row) => ({ id: row.id, name: row.name })));
      } catch {
        /* selects restent vides */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const filters = useMemo(() => {
    const parsed: Parameters<typeof generateGlobalInvoice>[0] = {
      company_id: Number(companyId),
    };
    if (hospitalId) parsed.hospital_id = Number(hospitalId);
    if (systemId.trim()) parsed.system_id = systemId.trim();
    if (actType) parsed.act_type = actType;
    if (startDate && endDate) {
      parsed.start_date = startDate;
      parsed.end_date = endDate;
    } else {
      parsed.month = Number(month);
      parsed.year = Number(year);
    }
    return parsed;
  }, [companyId, hospitalId, systemId, actType, startDate, endDate, month, year]);

  const handleGenerate = async () => {
    if (!companyId) {
      showToast({ message: "Sélectionnez une entreprise.", variant: "warning" });
      return;
    }
    if (!getAuthToken()) {
      showToast({ message: "Connexion API requise.", variant: "warning" });
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const generated = await generateGlobalInvoice(filters);
      setResult(generated);
      showToast({
        message: `${generated.summary.invoice_count} facture(s) agrégée(s).`,
        variant: "success",
      });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Génération impossible.";
      setError(message);
      showToast({ message, variant: "danger" });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!companyId || !getAuthToken()) {
      showToast({ message: "Générez d'abord une facture globale.", variant: "warning" });
      return;
    }
    try {
      await exportGlobalInvoiceCsv(filters);
    } catch (err) {
      showToast({
        message: err instanceof Error ? err.message : "Export CSV impossible.",
        variant: "danger",
      });
    }
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: labels.billing },
          { label: labels.globalTitle },
        ]}
      />

      <div className="theme-card">
        <div className="border-b border-border px-6 py-5">
          <h1 className="text-lg font-semibold text-foreground">{labels.filtersTitle}</h1>
          <p className="mt-1 text-sm text-muted">{labels.filtersSubtitle}</p>
        </div>

        <div className="space-y-6 p-6">
          {error && (
            <div className="rounded-xl border border-danger/20 bg-danger-soft px-4 py-3 text-sm text-danger">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <FormField label={t.safecheck.fields.company} required>
              <select
                className={selectClass}
                value={companyId}
                onChange={(event) => setCompanyId(event.target.value)}
              >
                <option value="">{labels.selectCompany}</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label={labels.month}>
              <select
                className={selectClass}
                value={month}
                onChange={(event) => setMonth(event.target.value)}
              >
                {monthOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label={labels.year}>
              <select
                className={selectClass}
                value={year}
                onChange={(event) => setYear(event.target.value)}
              >
                {yearOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FormField>
          </div>

          <div className="flex items-center gap-2 border-y border-border py-4 text-sm text-muted">
            <Calendar size={16} className="text-purple" />
            {labels.dateRange}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField label={labels.startDate} hint={labels.startDateHint}>
              <input
                type="date"
                className={inputClass}
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
              />
            </FormField>
            <FormField label={labels.endDate} hint={labels.endDateHint}>
              <input
                type="date"
                className={inputClass}
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField label={labels.systemId} hint={labels.systemIdHint}>
              <input
                className={inputClass}
                placeholder="Ex: SFC0003"
                value={systemId}
                onChange={(event) => setSystemId(event.target.value)}
              />
            </FormField>
            <FormField label={labels.provider}>
              <select
                className={selectClass}
                value={hospitalId}
                onChange={(event) => setHospitalId(event.target.value)}
              >
                <option value="">{labels.allProviders}</option>
                {hospitals.map((hospital) => (
                  <option key={hospital.id} value={hospital.id}>
                    {hospital.name}
                  </option>
                ))}
              </select>
            </FormField>
          </div>

          <FormField label={labels.actType}>
            <select
              className={selectClass}
              value={actType}
              onChange={(event) => setActType(event.target.value)}
            >
              {actTypeFilterOptions.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </FormField>

          {result && (
            <div className="rounded-xl border border-border bg-card-muted/30 p-4">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-base font-semibold text-foreground">Récapitulatif</h2>
                  <p className="text-sm text-muted">
                    Période {result.period.label} · {result.summary.invoice_count} facture(s)
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => void handleExport()}
                  className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground hover:bg-card-muted"
                >
                  <Download size={16} />
                  Exporter CSV
                </button>
              </div>
              <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-border bg-card px-4 py-3">
                  <p className="text-xs uppercase tracking-wide text-muted">Montant total</p>
                  <p className="text-lg font-semibold text-foreground">
                    {formatUsd(result.summary.total_amount_usd)}
                  </p>
                </div>
                <div className="rounded-lg border border-border bg-card px-4 py-3">
                  <p className="text-xs uppercase tracking-wide text-muted">Prise en charge</p>
                  <p className="text-lg font-semibold text-foreground">
                    {formatUsd(result.summary.total_covered_usd)}
                  </p>
                </div>
              </div>
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="safecheck-table w-full min-w-[720px]">
                  <thead className="bg-card-muted/60">
                    <tr className="border-b border-border">
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                        Réf.
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                        Prestataire
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                        Employé
                      </th>
                      <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted">
                        Montant
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                        Statut
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.invoices.map((invoice) => (
                      <tr key={invoice.id} className="border-b border-border last:border-0">
                        <td className="px-3 py-2 text-sm">{invoice.reference}</td>
                        <td className="px-3 py-2 text-sm">{invoice.hospital}</td>
                        <td className="px-3 py-2 text-sm">
                          {invoice.employee}
                          {invoice.employee_matricule ? (
                            <span className="text-muted"> · {invoice.employee_matricule}</span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 text-right text-sm font-medium">
                          {formatUsd(invoice.amount_usd)}
                        </td>
                        <td className="px-3 py-2 text-sm">{invoice.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-6 py-4">
          <Link
            to="/dashboard/admin"
            className="flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm text-muted-foreground hover:bg-card-muted"
          >
            <ArrowLeft size={16} />
            {t.safecheck.cancel}
          </Link>
          <button
            type="button"
            disabled={loading}
            onClick={() => void handleGenerate()}
            className="flex items-center gap-2 rounded-lg bg-purple px-4 py-2 text-sm font-medium text-white hover:bg-purple/90 disabled:opacity-60"
          >
            <FileText size={16} />
            {loading ? "Génération…" : labels.generate}
          </button>
        </div>
      </div>
    </>
  );
}
